import { UNGROUPED_RESOURCES_GROUP_ID } from '@/console/features/groups/components/group-list/group-list.constants';
import { api } from '@/shared/redux/api/_generated/api/assets-api.generated';
import { createEntityTagProviders } from '@/shared/redux/api/assets/assets-api.utils';
import { TRtkApiTags } from '@/shared/types/api.types';

type TagType = 'Groups' | 'Device' | 'Gateways' | 'Project';

const groupTags = createEntityTagProviders<TagType>(
  'Groups' as const,
  'groupId',
  'parentGroupId',
);

export const groupsApi = api.enhanceEndpoints({
  addTagTypes: ['Groups', 'Device', 'Gateways', 'Project'],
  endpoints: {
    ReadResourceGroupsToBuildTree: {
      providesTags: groupTags.listTags,
    },
    ReadAllResourceGroups: {
      providesTags: groupTags.listTags,
      /*
       * This is used, to populate the cache of the readResourceGroupById query
       * to avoid fetching the same data twice.
       * This is especially important for root groups
       * */
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Only pre-warm the sub-group cache for root-group loads (the /groups tree).
        // Non-root callers (home cards, filters, selects) render only counts and would
        // otherwise trigger one ReadSubGroupsForResourceGroup per row (an N+1).
        if (!arg?.isRoot) return;

        const { data } = await queryFulfilled;
        const items = data?.readAllResourceGroups.items;

        if (items) {
          await Promise.all(
            items.map(async (group) => {
              const subGroupsResult = await dispatch(
                api.endpoints.ReadSubGroupsForResourceGroup.initiate({
                  groupId: group.groupId,
                }),
              );
              const individualResponse = {
                readResourceGroup: {
                  ...group,
                },
                readSubGroupsForResourceGroup: {
                  ...(subGroupsResult?.data?.readSubGroupsForResourceGroup ?? {
                    items: [],
                    pages: 0,
                    itemCount: 0,
                  }),
                },
              };
              dispatch(
                api.util.upsertQueryData(
                  'ReadResourceGroupById',
                  { groupId: group.groupId },
                  individualResponse,
                ),
              );
            }),
          );
        }
      },
    },
    ReadResourceGroupById: {
      providesTags: (result, __, arg) => {
        const tags: TRtkApiTags<TagType> = [
          { type: 'Groups' as const, id: 'LIST' },
          { type: 'Groups' as const, id: arg.groupId },
        ];

        if (result?.readSubGroupsForResourceGroup) {
          result?.readSubGroupsForResourceGroup.items?.forEach((subgroup) => {
            tags.push({
              type: 'Groups' as const,
              id: subgroup.groupId,
            });
          });
        }

        return tags;
      },
    },
    CreateResourceGroup: {
      invalidatesTags: (result) => {
        const newGroupParentId =
          result?.createResourceGroup.parentGroupId ?? '';

        const tags = [{ type: 'Groups' as const, id: 'LIST' }];

        if (newGroupParentId) {
          tags.push({ type: 'Groups' as const, id: newGroupParentId });
        }

        return tags;
      },
    },
    UpdateResourceGroup: {
      invalidatesTags: ['Groups'],
    },
    DeleteResourceGroup: {
      invalidatesTags: [{ type: 'Groups' as const, id: 'LIST' }],
    },
    ReadGroupedAssetsCount: {
      providesTags: (_, __, arg) => [
        { type: 'Groups' as const, id: arg.groupId },
      ],
    },
    ReadGroupedAssetCounts: {
      providesTags: (result) =>
        (result?.readGroupedAssetCounts ?? []).map((count) => ({
          type: 'Groups' as const,
          id: count.groupId,
        })),
    },
    ReadUngroupedAssetsCount: {
      providesTags: [
        { type: 'Groups' as const, id: UNGROUPED_RESOURCES_GROUP_ID },
      ],
    },
  },
});

export const {
  useReadResourceGroupByIdQuery,
  useDeleteResourceGroupMutation,
  useCreateResourceGroupMutation,
  useReadAllResourceGroupsQuery,
  useUpdateResourceGroupMutation,
  useReadResourceGroupNamesForRoleRowQuery,
  useReadResourceGroupNameByIdQuery,
  useLazyReadResourceGroupNameByIdQuery,
  useReadAllResourceGroupsWithoutPermissionChecksQuery,
  useReadResourceGroupsToBuildTreeQuery,
  useReadSubGroupsForResourceGroupQuery,
  useReadGroupedAssetsCountQuery,
  useReadGroupedAssetCountsQuery,
  useReadUngroupedAssetsCountQuery,
} = groupsApi;

export type TResourceGroupsApi = typeof groupsApi;
